"use strict";

var $ = require('lc-jquery');
var each = require('lodash.foreach');
var Emitter = require('events').EventEmitter;
var inherits = require('inherits');
var FormValidator = require('ui-form-validator');
var Spinner = require('ui-spinner');

/**
 * Represents a form manager.
 *
 * @constructor
 * @param {Object} form - A jQuery instance of the form
 * @param {Object} options - Options that specifies form schema,
 *                 service endPoint, errorHandler, submitControl,
 *                 nativeSubmit, allowTracking, hideErrors, and
 *                 spinner usage
 */
function FormManager(form, options) {
  this.form = form;
  this.options = options || {};
  this.options.endPoint = this.options.endPoint || {};
  // eslint-disable-next-line no-unused-expressions
  this.perFieldValidation; // init variable
  this.options.enablePerFieldValidation = this.options.enablePerFieldValidation || false;
  if (this.options.spinner) {
    this.spinner = new Spinner($('body'));
  }
  if (this.options.allowTracking === undefined) {
    this.options.allowTracking = true;
  }
  if (this.options.endPoint.nativeSubmit) {
    this.nativeSubmitForm = $('<form style="display:none;"></form>').appendTo('body');
  }

  //  register to disable button properly before instantiating formValidator below (which also listens for submit)
  this.form.on('submit', $.proxy(this.disableSubmitControl, this));
  this.formValidator = new FormValidator(this.form, {
    formatEventTrigger: this.options.formatEventTrigger,
    validatorSchema: this.options.validatorSchema || this.options.schema,
    formatterSchema: this.options.formatterSchema,
    transformFunction: this.options.transformFunction
  });
  if (this.options.enablePerFieldValidation) {
    this.attachListeners();
    this.formValidator.on('successFields', $.proxy(this.removeFieldError, this));
  }
  this.formValidator.on('error', $.proxy(this.handleValidationErrors, this));
  this.formValidator.on('success', $.proxy(this.process, this));
}
inherits(FormManager, Emitter);

/**
 * attach listeners to the form elements
 */
FormManager.prototype.attachListeners = function attachListeners() {
  var self = this;
  var events = {
    'input[type="text"]': 'focusout',
    'input[type="password"]': 'focusout',
    'input[type="ssn"]': 'focusout',
    'input[type="tel"]': 'focusout',
    'input[type="email"]': 'focusout',
    'input[type="number"]': 'focusout',
    select: 'change'
  };
  // reset error message upon focus in for inputs
  this.form.on('focusin change', 'input', function (e) {
    self.removeFieldError([e.target.name]);
  });
  // trigger field validation for all
  each(events, function (event, selector) {
    self.form.on(event, selector, function (e) {
      self.validateField([e.target.name]);
    });
  });
};

/**
 * handler to validateField
 */
FormManager.prototype.validateField = function validateField(fieldArray) {
  this.formValidator.validate(fieldArray, this.options.validatorSchema);
};

/**
 * Hide a validation error.
 * @function removeFieldError
 * @memberof FormManager
 * @instance
 * @callback FormValidator#on
 * @param {string} name - name of input element with error.
 */
FormManager.prototype.removeFieldError = function removeFieldError(fieldNames) {
  var form = this.form;
  each(fieldNames, function iterateFieldNames(fieldName) {
    form.find("[name=".concat(fieldName, "]")).removeClass('has-error');
    form.find("[data-error-for=".concat(fieldName, "]")).remove();
  });
};

/**
 * handler to disable submit button to prevent multiple submissions input
 */
FormManager.prototype.disableSubmitControl = function disableSubmitControl() {
  this.resetErrors();
  if (this.options.submitControl) {
    this.options.submitControl.prop('disabled', true);
  }
};

/**
 * handler to enable submit button after submission failed
 * and some validation occurs, giving user chance to retry
 * input
 */
FormManager.prototype.unmaskForm = function unmaskForm() {
  if (this.spinner) {
    this.spinner.hide();
  }
  if (this.options.submitControl) {
    this.options.submitControl.prop('disabled', false);
  }
};
FormManager.prototype.markError = function markError($el, message) {
  $el.addClass('has-error');
  $el.parent().addClass('has-error');
  if (message) {
    var errorMsg = "<div class=\"error-message\" data-error-for=\"".concat($el.attr('name'), "\">") + "<span class=\"icon icon-form-error error-image\"></span>" + "<span class=\"error-text\" data-aid=\"error-text\">".concat(message, "</span>") + "</div>";
    var customTarget = $el.data('error-target');
    if (customTarget) {
      this.form.find("[data-error-id=".concat(customTarget, "]")).append(errorMsg);
    } else {
      $el.after(errorMsg);
    }
  }
};
FormManager.prototype.resetErrors = function resetErrors() {
  this.form.find('.error-message').remove(); //  remove messages
  //  remove has-error on form and descendents
  this.form.removeClass('has-error');
  this.form.removeAttr('data-form-error');
  this.form.find('.has-error').removeClass('has-error');
};

/**
 * Track validation errors with lcTracking.
 *
 * @function trackErrors
 * @memberof FormManager
 * @instance
 * @param {ARRAY} error keys- Form validation errors keys.
 * @param {ARRAY} error messages- Form validation errors messages.
 */
FormManager.prototype.trackErrors = function trackErrors(keys, messages) {
  window.lcTracking.trackCustomEvent({
    eventName: 'Funnel Error',
    ERROR_KEYS: keys.join('::'),
    ERROR_MESSAGES: messages.join('::')
  });
};

/**
 * Handle validation errors.
 *
 * @function handleValidationErrors
 * @memberof FormManager
 * @instance
 * @callback FormValidator#on
 * @emits validationError
 * @param {Object} error - Form validation errors.
 */
FormManager.prototype.handleValidationErrors = function handleValidationErrors(errors) {
  var errorKeys = [];
  var errorMessages = [];
  var _this = this;
  if (!this.options.enablePerFieldValidation) {
    this.resetErrors();
  }
  this.form.attr('data-form-error', true);
  if (errors) {
    each(errors, function (errorInternal, key) {
      each(errorInternal, function (error) {
        if (error !== undefined) {
          //  option to check if errors displayed inline on form
          if (!_this.options.hideErrors) {
            _this.markError(_this.form.find("[name=".concat(key, "]")), error);
          }
          errorKeys.push(key);
          errorMessages.push(error);
        }
      });
    });
    if (this.options.allowTracking && window.lcTracking) {
      this.trackErrors(errorKeys, errorMessages);
    }
  }

  //  re-enable form for the user
  this.unmaskForm();
  this.emit('validationError', errors);
};

// backwards compatibility
FormManager.prototype.displayValidationErrors = FormManager.prototype.handleValidationErrors;

/**
 * Invokes service endpoint upon successful form submit.
 *
 * @function process
 * @memberof FormManager
 * @instance
 * @callback FormValidator#on
 * @param {Object} payload - Data payload to be POSTed to the service endpoint.
 */
// eslint-disable-next-line consistent-return
FormManager.prototype.process = function process(payload) {
  this.resetErrors();
  var submitData = true;
  var preventDefault = function preventDefault() {
    submitData = false;
  };
  this.emit('validationSuccess', {
    preventDefault: preventDefault
  });
  if (submitData) {
    if (this.spinner) {
      this.spinner.show();
    }
    var endPoint = this.options.endPoint;

    // semi-intelligent defaults
    endPoint.method = endPoint.method || this.form.attr('method') || 'POST';
    endPoint.url = endPoint.url || this.form.attr('action') || window.location.href;
    return endPoint.nativeSubmit ? this.submitAsForm(payload || {}, endPoint) : this.submitAsAjax(payload, endPoint);
  }
};
FormManager.prototype.submitAsForm = function submitAsForm(payload, endPoint) {
  // Assemble a new form to redirection

  // reset from any previous submit
  this.nativeSubmitForm.empty();
  this.nativeSubmitForm.attr('action', endPoint.url);
  this.nativeSubmitForm.attr('method', endPoint.method);

  // Append csrf token to payload if POST
  if (endPoint.method === 'POST') {
    // eslint-disable-next-line no-param-reassign
    payload.csrf = $('meta[name=csrf-token]').attr('content');
  }

  // Append validated and 'unformatted' (formatted for submit) data as input fields to the form
  var self = this;
  each(payload, function (val, key) {
    $('<input type="hidden">').attr('name', key).attr('value', val).appendTo(self.nativeSubmitForm);
  });
  return this.nativeSubmitForm.submit();
};
FormManager.prototype.submitAsAjax = function submitAsAjax(payload, endPoint) {
  return $.ajax({
    type: endPoint.method,
    dataType: endPoint.dataType || 'json',
    contentType: endPoint.contentType,
    data: payload,
    processData: endPoint.processData,
    // by default, don't convert payload to a string
    url: endPoint.url,
    error: $.proxy(this.ajaxErrorHandler, this),
    success: $.proxy(this.ajaxHandler, this)
  });
};

/**
 * Ajax Response handler for service invocation.
 *
 * @function ajaxHandler
 * @memberof FormManager
 * @instance
 * @callback $.ajax
 * @emits success
 * @param {Object} data - Service response payload.
 */
FormManager.prototype.ajaxHandler = function ajaxHandler(data) {
  this.emit('success', data);
  //  re-enable in case 'success' doesn't wipe the page
  this.unmaskForm();
};

/**
 * Ajax Error Response handler for service invocation.
 *
 * @function errorHandler
 * @memberof FormManager
 * @instance
 * @callback $.ajax
 * @emits serverError
 * @param {Object} jqXHR - jQuery ajax error object
 */
FormManager.prototype.ajaxErrorHandler = function ajaxErrorHandler(jqXHR, textStatus, errorThrown) {
  // This is to handle boreas schema validation error response if client side validation is compromised
  if (jqXHR.status === 400 && jqXHR.responseJSON && jqXHR.responseJSON.errors) {
    // per https:// wiki.tlcinternal.com/display/ARC/Microservice+Error+Messages+and+HTTP+Response+Codes
    this.handleValidationErrors(jqXHR.responseJSON.errors); // TODO, bake in ui-notification-box?  TBD...
  } else {
    this.unmaskForm();
  }
  if (this.options.errorHandler) {
    this.options.errorHandler(jqXHR, textStatus, errorThrown);
  }
};
module.exports = FormManager;
